import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const preparations = [
  {
    title: 'gBizIDの取得',
    content: [
      '補助金の申請には「gBizID」というものが必須となります。',
      'gBizIDの取得には2週間程度のお時間が掛かるため、早めの取得をお勧めいたします。',
      'また、取得することにより、様々な補助金の情報が得られます。',
    ],
    link: { text: 'gBizID公式サイト', url: 'https://gbiz-id.go.jp/' },
  },
  {
    title: 'SECURITY ACTIONのお申し込み',
    content: [
      'IT導入補助金の申請要件として、「SECURITY ACTION」のお申し込みが必要です。',
      '中小企業自らが情報セキュリティ対策に取り組むことを自己宣言する制度となります。',
    ],
    links: [
      { text: '「SECURITY ACTION」の概要説明', url: 'https://www.ipa.go.jp/security/security-action/it-hojo.html' },
      { text: '「SECURITY ACTION」のお申し込み', url: 'https://security-shien.ipa.go.jp/security/entry/' },
    ],
  },
  {
    title: '証明書準備',
    content: [
      'IT導入支援事業者登録申請を開始するにあたり、事前に必要な書類となります。',
    ],
    details: [
      {
        label: '法人様の場合',
        items: [
          '履歴事項全部証明書（発行から3か月以内のもの）',
          '税務署窓口で発行された直近の法人税納税証明「その1」もしくは「その2」',
        ],
      },
      {
        label: '個人事業主の場合',
        items: [
          '運転免許証コピー（有効期限内のもの）もしくは住民票（発行から3か月以内のもの）',
          '税務署窓口で発行された直近の所得税納税証明「その1」もしくは「その2」',
          '直近分の確定申告書Bの控え（税務署の受領印か受信通知のあるもの）',
        ],
      },
    ],
    link: { text: '詳細はこちらのPDFをご確認ください', url: 'https://www.it-hojo.jp/r01/doc/pdf/r1_shien_apply_example.pdf' },
  },
];

const ITSupportPreparation = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const navigate = useNavigate();

  const toggleAccordion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-14">
          <p className="text-[#5DAA68] text-xs font-semibold tracking-[0.2em] uppercase mb-3 font-['Inter']">
            Preparation
          </p>
          <h4 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#89B56B] font-['Noto_Sans_JP'] whitespace-nowrap">
              助成金申請に関してのご準備
          </h4>
        </div>

        <div className="space-y-3">
          {preparations.map((item, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl shadow-[0_2px_12px_rgba(137,181,107,0.06)] overflow-hidden border border-gray-100"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-[#F8FBF6] transition-colors"
              >
                <span className="text-sm font-bold text-[#3F6844] font-['Noto_Sans_JP']">{item.title}</span>
                <div className="w-6 h-6 flex items-center justify-center text-[#89B56B] flex-shrink-0">
                  <i className={`ri-arrow-down-s-line text-xl transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`}></i>
                </div>
              </button>

              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="px-6 pb-6 pt-0">
                  <div className="border-t border-gray-100 pt-4">
                    {item.content.map((text, i) => (
                      <p key={i} className="text-[13px] text-gray-700 leading-[1.9] mb-1">{text}</p>
                    ))}

                    {item.details && (
                      <div className="mt-4 space-y-4">
                        {item.details.map((detail, di) => (
                          <div key={di}>
                            <p className="text-[13px] font-bold text-[#3F6844] mb-2">● {detail.label}</p>
                            <ul className="space-y-1 pl-4">
                              {detail.items.map((detailItem, dii) => (
                                <li key={dii} className="text-[13px] text-gray-700 leading-[1.9] flex items-start gap-1.5">
                                  <span className="text-[#89B56B] mt-0.5 flex-shrink-0">・</span>
                                  {detailItem}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}

                    {item.link && (
                      <a
                        href={item.link.url}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="inline-flex items-center gap-1.5 text-[#5DAA68] hover:text-[#3F6844] text-[13px] font-medium transition-colors cursor-pointer mt-3"
                      >
                        <i className="ri-external-link-line text-sm"></i>
                        {item.link.text}
                      </a>
                    )}

                    {item.links && (
                      <div className="mt-3 space-y-1.5">
                        {item.links.map((link, li) => (
                          <a
                            key={li}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer nofollow"
                            className="flex items-center gap-1.5 text-[#5DAA68] hover:text-[#3F6844] text-[13px] font-medium transition-colors cursor-pointer"
                          >
                            <i className="ri-external-link-line text-sm"></i>
                            {link.text}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-14 text-center">
          <button
            onClick={() => navigate('/contact')}
            className="inline-flex items-center gap-2 bg-[#89B56B] text-white px-12 py-4 rounded-full font-medium text-base hover:bg-[#5DAA68] transition-all duration-300 shadow-lg shadow-[#89B56B]/20 cursor-pointer whitespace-nowrap"
          >
            お問い合わせ
          </button>
        </div>
      </div>
    </section>
  );
};

export default ITSupportPreparation;
